import { clsx } from 'clsx';
import { MouseEventHandler, useEffect, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useLocation } from 'react-router';

import { useGetGoodTariffsData, useGetPrettyNumbersData } from '@/api';
import { ClearIcon } from '@/components/icons/input';
import { Categories } from '@/components/PrettyNumbers/Filters/Categories/Categories.tsx';
import { DeviceType } from '@/components/PrettyNumbers/Filters/DeviceType/DeviceType.tsx';
import { FavNumInput } from '@/components/PrettyNumbers/Filters/FavNumInput/FavNumInput.tsx';
import { NumberInput } from '@/components/PrettyNumbers/Filters/NumberInput/NumberInput.tsx';
import { Operators } from '@/components/PrettyNumbers/Filters/Operators/Operators.tsx';
import { PriceInputs } from '@/components/PrettyNumbers/Filters/PriceInputs/PriceInputs.tsx';
import { Button } from '@/components/shared/Button';
import {
    GroupHeadingTariffs,
    GroupTariffs,
    MenuListTariffs,
    MultiValueContainerRegions,
    MultiValueContainerTariffs,
} from '@/components/shared/Dropdown/constants.tsx';
import { Dropdown } from '@/components/shared/Dropdown/Dropdown.tsx';
import { appRoutes } from '@/routes/app-routes.ts';
import { useFilterStore } from '@/store';
import { DropdownOptions } from '@/types';
import {
    getDropdownOptions,
    getOperatorsIconsNames,
    getPrefixOptions,
    getTariffsOptions,
    sortRegions,
} from '@/utils';

import cn from './styles.module.sass';

interface IFiltersProps {
    isGoodTariffs?: boolean;
}

export const Filters = ({ isGoodTariffs = false }: IFiltersProps) => {
    const { pathname } = useLocation();
    const { data: goodTariffsData, isLoading: isGoodTariffsLoading } = useGetGoodTariffsData();
    const { data: prettyNumsData, isLoading: isPrettyNumsLoading } = useGetPrettyNumbersData();
    const {
        setCurrentPage,
        setPrevPage,
        isFiltersOpen,
        setIsFiltersOpen,
        setSearchParams,
        categories,
        setCategories,
        operators,
        setOperators,
        setSearchParamsStr,
        setPrevSearchParamsStr,
        priceMin,
        priceMax,
        tariffsVal,
        setTariffsVal,
        regionsVal,
        setRegionsVal,
        clearAllFilters,
        clearSearchParamsStr,
    } = useFilterStore();

    const prefixOptions = useMemo(
        () => getPrefixOptions(prettyNumsData?.prefixes || []),
        [prettyNumsData?.prefixes]
    );

    const regions = useMemo(
        () => [
            {
                label: 'Выбрать регион',
                options: sortRegions(getDropdownOptions(prettyNumsData?.regions || [])),
            },
        ],
        [prettyNumsData?.regions]
    );

    const tariffs = useMemo(
        () => getTariffsOptions(prettyNumsData?.tariffs || [], prettyNumsData?.operators || []),
        [prettyNumsData?.operators, prettyNumsData?.tariffs]
    );

    const handleCategoryClick = (id: number) => {
        const targetCategory = categories?.find(category => category.id === id);
        if (targetCategory && categories) {
            const updatedCategory = {
                ...targetCategory,
                active: !targetCategory.active,
            };
            const newCategories = categories.map(category =>
                category.id === id ? updatedCategory : category
            );

            setCategories(newCategories);

            const activeCategoriesIds = newCategories
                .filter(category => category.active)
                .map(category => category.id);
            setSearchParams({ categories: activeCategoriesIds });
        }
    };

    const handleOperatorClick = (id: number) => {
        const targetOperator = operators?.find(operator => operator.id === id);
        if (targetOperator && operators) {
            const updatedOperator = {
                ...targetOperator,
                active: !targetOperator.active,
            };
            const newOperators = operators.map(operator =>
                operator.id === id ? updatedOperator : operator
            );

            setOperators(newOperators);

            const activeOperatorsIds = newOperators
                .filter(operator => operator.active)
                .map(operator => operator.id);
            setSearchParams({ operators: activeOperatorsIds });
        }
    };

    const handleRegionChange = (newValue: unknown) => {
        const regionsValues = newValue as DropdownOptions<number>;
        const regionsIds = regionsValues.map(region => region.value);
        setRegionsVal(regionsValues);
        setSearchParams({ regions: regionsIds });
    };

    const handleTariffChange = (newValue: unknown) => {
        const tariffsValues = newValue as DropdownOptions<string>;
        const tariffsIds = tariffsValues.map(tariff => tariff.value);
        setTariffsVal(tariffsValues);
        setSearchParams({ tariffs: tariffsIds });
    };

    const handleSearch: MouseEventHandler<HTMLButtonElement> = () => {
        setCurrentPage(0);
        setPrevPage(0);
        setIsFiltersOpen(false);
        setPrevSearchParamsStr();
        setSearchParamsStr();
    };

    const handleClearFilters: MouseEventHandler<HTMLButtonElement> = () => {
        clearAllFilters();
    };

    const handleCloseFilters = () => {
        setIsFiltersOpen(false);
    };

    useEffect(() => {
        if (prettyNumsData?.categories) {
            const categoriesWithStatuses = prettyNumsData.categories.map(category => ({
                ...category,
                active: false,
            }));
            setCategories(categoriesWithStatuses);
        }

        const operatorsData = prettyNumsData?.operators || goodTariffsData?.operators;
        if (operatorsData) {
            const operatorsWithStatuses = getOperatorsIconsNames(operatorsData);
            setOperators(operatorsWithStatuses);
        }

        return () => {
            clearAllFilters();
        };
    }, [goodTariffsData?.operators, prettyNumsData?.categories, prettyNumsData?.operators]);

    useEffect(() => {
        return () => {
            clearSearchParamsStr();
            setIsFiltersOpen(false);
        };
    }, []);

    return (
        <div
            className={clsx(
                cn.filters,
                !isFiltersOpen && cn.hide,
                pathname.includes(appRoutes.goodTariffs) && cn.noMargin
            )}
        >
            <Button onClick={handleClearFilters} className={cn.clearBtn} background="transparent">
                Очистить {ClearIcon}
            </Button>
            <div className={cn.filtersHead}>
                <div className={cn.title}>
                    <div onClick={handleCloseFilters} className={cn.backBtn}>
                        <img src="/images/main/arrow_dark.svg" alt="" />
                    </div>
                    <h3>Фильтры</h3>
                </div>

                {!isGoodTariffs ? (
                    <p>Выбери красивый номер удобно и быстро с помощью фильтров</p>
                ) : (
                    <p>Выбери лучший тариф и подключи номер</p>
                )}
            </div>
            {!isGoodTariffs ? (
                <>
                    <div className={cn.filterBlock}>
                        <p className={cn.filterTitle}>По цифрам:</p>
                        <NumberInput options={prefixOptions} />
                    </div>
                    <div className={cn.filterBlock}>
                        <p className={cn.filterTitle}>Любимое число:</p>
                        <FavNumInput />
                    </div>
                    <div className={cn.filterBlock}>
                        <p className={cn.filterTitle}>Категория:</p>
                        <Categories
                            isLoading={isPrettyNumsLoading}
                            onClick={handleCategoryClick}
                            categories={categories || []}
                        />
                    </div>
                </>
            ) : (
                <div className={cn.filterBlock}>
                    <p className={cn.filterTitle}>Устройство:</p>
                    <DeviceType />
                </div>
            )}
            <div className={cn.filterBlock}>
                <p className={cn.filterTitle}>Оператор:</p>
                <Operators
                    isLoading={isPrettyNumsLoading || isGoodTariffsLoading}
                    onClick={handleOperatorClick}
                    operatorsIconsNames={operators || []}
                />
            </div>
            {!isGoodTariffs && (
                <div className={clsx(cn.filterBlock, cn.regionTariff)}>
                    <div>
                        <p className={cn.filterTitle}>Регион:</p>
                        {isPrettyNumsLoading ? (
                            <Skeleton width={161} height={38} />
                        ) : (
                            <Dropdown
                                options={regions}
                                isMulti
                                value={regionsVal}
                                classNames={{
                                    container: () => cn.dropdownContainer,
                                    control: () => cn.dropdownControl,
                                }}
                                placeholder="Выбрать регион"
                                onChange={handleRegionChange}
                                closeMenuOnSelect={false}
                                components={{ MultiValueContainer: MultiValueContainerRegions }}
                            />
                        )}
                    </div>
                    <div>
                        <p className={cn.filterTitle}>Тариф:</p>
                        {isPrettyNumsLoading ? (
                            <Skeleton width={161} height={38} />
                        ) : (
                            <Dropdown
                                options={tariffs}
                                isMulti
                                value={tariffsVal}
                                closeMenuOnSelect={false}
                                placeholder="Выбрать тариф"
                                onChange={handleTariffChange}
                                classNames={{
                                    menu: () => cn.tariffMenu,
                                    container: () => cn.dropdownContainer,
                                    control: () => cn.dropdownControl,
                                }}
                                components={{
                                    MultiValueContainer: MultiValueContainerTariffs,
                                    MenuList: MenuListTariffs,
                                    GroupHeading: GroupHeadingTariffs,
                                    Group: GroupTariffs,
                                }}
                            />
                        )}
                    </div>
                </div>
            )}
            <div className={cn.filterBlock}>
                <p className={cn.filterTitle}>Стоимость:</p>
                <PriceInputs priceMin={priceMin} priceMax={priceMax} />
            </div>
            <div className={cn.submitBtn}>
                <Button disabled={parseInt(priceMax) < parseInt(priceMin)} onClick={handleSearch}>
                    Поиск
                </Button>
            </div>
        </div>
    );
};
